/**
 * AI jobs where they matter: a determinate progress banner on the page while jobs run (you
 * keep working), a strip on the item a job is about, and the full list with cost in the
 * review tab. Every running job can be cancelled; a finished one opens its proposal. A cancel
 * or clear that fails says so on the job, not in a toast.
 */
import { Bot, CircleCheck, CircleX, Clock, Eye, LoaderCircle, Play, Server, Sparkles, Square, Trash2, X } from '@orgus/ui/icons';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { AI_TASK_LABEL, isJobLive } from '@orgus/protocol';
import type { AiJob, Id } from '@orgus/protocol';
import { Button, ErrorState, IconButton, MeterBar, Popover, PopoverContent, PopoverTrigger, REVEAL_GROUP, RelativeTime, RevealRest, RevealSlot, cn, formatInt, formatMoney, toast, useNow } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { toUiError } from '../../lib/api.ts';
import { useMachines, useOrgus } from '../../lib/store.ts';
import { cancelJob, clearJob } from './data.ts';
import { jobLine, jobProgress } from './model.ts';
import { HoldNote, useHold } from '../project-health/Holds.tsx';
import { startHoldNow } from '../project-health/api.ts';
import { holdText } from '../project-health/model.ts';

/** A queued job that yields to a landing or deploy says so, with Start now; a running one shows its progress. */
/** A held job has not started: it says Waiting, not a stage it has not reached. */
function JobLine({ job, now }: { job: AiJob; now: number }) {
  const hold = useHold('ai-job', job.state === 'queued' ? { id: job.id, label: job.title, projectId: job.projectId, machineId: job.machineId } : null);
  return <>{hold ? 'Waiting' : jobLine(job, now)}</>;
}

function JobHold({ job }: { job: AiJob }) {
  const hold = useHold('ai-job', job.state === 'queued' ? { id: job.id, label: job.title, projectId: job.projectId, machineId: job.machineId } : null);
  return hold ? <HoldNote hold={hold} className="px-4 pb-2" /> : null;
}

function JobIcon({ job }: { job: AiJob }) {
  const hold = useHold('ai-job', job.state === 'queued' ? { id: job.id, label: job.title, projectId: job.projectId, machineId: job.machineId } : null);
  if (hold) return <Clock className="size-4 shrink-0 text-fg-subtle" aria-label="Waiting" />;
  if (isJobLive(job.state)) return <LoaderCircle className="size-4 shrink-0 text-status-working motion-safe:animate-spin" aria-label="Running" />;
  if (job.state === 'done') return <CircleCheck className="size-4 shrink-0 text-fg-subtle" aria-label="Done" />;
  if (job.state === 'failed') return <CircleX className="size-4 shrink-0 text-status-danger" aria-label="Failed" />;
  return <Square className="size-4 shrink-0 text-fg-subtle" aria-label="Cancelled" />;
}

/** Cancel and clear, with the failure kept beside the job it was about. */
function useJobActions() {
  const [error, setError] = useState<{ id: Id; error: UiError } | null>(null);
  const run = async (j: AiJob, fn: () => Promise<unknown>, done?: string) => {
    setError(null);
    try {
      await fn();
      if (done) toast.success(done, j.title);
    } catch (e) {
      setError({ id: j.id, error: toUiError(e) });
    }
  };
  return {
    cancel: (j: AiJob) => void run(j, () => cancelJob(j.id), 'Job cancelled'),
    clear: (j: AiJob) => void run(j, () => clearJob(j.id)),
    errorFor: (id: Id) => (error?.id === id ? error.error : null),
    dismiss: () => setError(null),
  };
}

/**
 * One job on the item it is about, as a slim quiet line (a past job is a fact, not a card):
 * icon, what it did, how it went, and its one action. A running job adds a thin meter; a failed
 * one says why inline.
 */
export function JobStrip({ job, onReview }: { job: AiJob; onReview: (proposalId: Id) => void }) {
  const now = useNow(1000);
  const live = isJobLive(job.state);
  const actions = useJobActions();
  const err = actions.errorFor(job.id);
  return (
    <div className="flex flex-col gap-1" aria-live="polite">
      <div className="flex h-control-sm items-center gap-2">
        <JobIcon job={job} />
        <span className={cn('min-w-0 truncate text-ui', job.state === 'failed' ? 'text-fg' : 'text-fg-muted')}>{AI_TASK_LABEL[job.task]}</span>
        <span className="min-w-0 flex-1 truncate text-caption text-fg-subtle num">{jobLine(job, now)}</span>
        {live ? (
          <Button variant="ghost" size="sm" onClick={() => actions.cancel(job)}>
            Cancel
          </Button>
        ) : job.proposalId ? (
          <Button variant="ghost" size="sm" icon={<Eye />} onClick={() => onReview(job.proposalId!)}>
            Review
          </Button>
        ) : null}
      </div>
      {live && <MeterBar value={jobProgress(job)} tone="working" label={`${AI_TASK_LABEL[job.task]} progress`} className="ml-6" />}
      {job.state === 'failed' && job.error && (
        <p className="ml-6 text-caption text-fg-muted">
          {job.error.why} {job.error.next}
        </p>
      )}
      {err && <ErrorState variant="inline" error={err} />}
    </div>
  );
}

/** The page banner: every running job with its progress, and the last few that finished with something to review. */
export function AiJobsBanner({ jobs, onReview, onOpenAll, bare }: { jobs: readonly AiJob[]; onReview: (proposalId: Id) => void; onOpenAll: () => void; /** Inside a popover: no card of its own. */ bare?: boolean }) {
  const now = useNow(1000);
  const machines = useMachines();
  const actions = useJobActions();
  const live = jobs.filter((j) => isJobLive(j.state));
  const fresh = jobs.filter((j) => j.state === 'done' && j.proposalId && j.endedAt && now - j.endedAt < 2 * 60_000);
  const failed = jobs.filter((j) => j.state === 'failed' && j.endedAt && now - j.endedAt < 60_000);
  const shown = [...live, ...fresh, ...failed].slice(0, 3);
  if (!shown.length) return null;
  return (
    <div role="status" aria-label="AI jobs" className={cn('flex flex-col gap-1', !bare && 'rounded-card border border-border bg-card p-1 surface-sheen shadow-elevation-flat')}>
      {shown.map((j) => {
        const machine = machines.find((m) => m.id === j.machineId)?.name ?? 'a machine';
        const err = actions.errorFor(j.id);
        return (
          <div key={j.id} className="motion-arrive flex flex-col gap-2 rounded-control px-3 py-2">
            <div className="flex items-center gap-3">
              <JobIcon job={j} />
              <span className="min-w-0 flex-1 truncate text-ui text-fg">
                {j.title}
                <span className="text-fg-subtle">
                  {' '}
                  on {machine} with {j.model}
                </span>
              </span>
              {!bare && isJobLive(j.state) && j.state !== 'queued' && <MeterBar value={jobProgress(j)} tone="working" label={`${j.title} progress`} className="hidden w-32 sm:block" />}
              <span className="hidden text-caption text-fg-subtle num sm:inline">
                <JobLine job={j} now={now} />
              </span>
              {isJobLive(j.state) ? (
                <Button variant="ghost" size="sm" onClick={() => actions.cancel(j)}>
                  Cancel
                </Button>
              ) : j.proposalId ? (
                <Button variant="primary" size="sm" icon={<Eye />} onClick={() => onReview(j.proposalId!)}>
                  Review {j.summary ? `(${j.summary.replace(/ proposed$/, '')})` : ''}
                </Button>
              ) : (
                <Button variant="ghost" size="sm" onClick={onOpenAll}>
                  See why
                </Button>
              )}
            </div>
            {j.state === 'failed' && j.error && (
              <p className="ml-6 text-caption text-fg-muted">
                {j.error.why} {j.error.next}
              </p>
            )}
            <JobHold job={j} />
            {err && <ErrorState variant="inline" error={err} />}
          </div>
        );
      })}
      {live.length + fresh.length + failed.length > shown.length && (
        <div className="px-2 py-1">
          <Button variant="ghost" size="sm" onClick={onOpenAll}>
            {live.length + fresh.length + failed.length - shown.length} more in Review
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * AI jobs folded into one quiet control for the page header: "2 AI jobs" while they run, "Review
 * proposal" once one left something to decide, the rows (progress, Cancel, Review) one click
 * away. Nothing shows when no job is running or freshly finished.
 */
export function AiJobsMenu({ jobs, onReview, onOpenAll }: { jobs: readonly AiJob[]; onReview: (proposalId: Id) => void; onOpenAll: () => void }) {
  const now = useNow(1000);
  const live = jobs.filter((j) => isJobLive(j.state));
  const fresh = jobs.filter((j) => j.state === 'done' && j.proposalId && j.endedAt && now - j.endedAt < 2 * 60_000);
  const failed = jobs.filter((j) => j.state === 'failed' && j.endedAt && now - j.endedAt < 60_000);
  if (!live.length && !fresh.length && !failed.length) return null;
  const ready = fresh.length === 1 && !live.length;
  // A failure names what failed ("Draft item failed"); its why and the one thing to do are in the
  // tooltip and the popover, never an unexplained red chip (R-FN-8).
  const failedLabel = failed.length === 1 ? `${AI_TASK_LABEL[failed[0]!.task]} failed` : `${formatInt(failed.length)} AI jobs failed`;
  const label = ready ? 'Review proposal' : live.length ? `${formatInt(live.length)} AI ${live.length === 1 ? 'job' : 'jobs'}` : fresh.length ? `${formatInt(fresh.length)} proposals ready` : failedLabel;
  const failedHint = !live.length && !fresh.length && failed.length === 1 && failed[0]!.error ? `${failed[0]!.error.why} ${failed[0]!.error.next}` : undefined;
  const icon = live.length ? <LoaderCircle className="text-status-working motion-safe:animate-spin" /> : failed.length && !fresh.length ? <CircleX className="text-status-danger" /> : <Sparkles />;
  if (ready) {
    return (
      <Button variant="ghost" size="sm" icon={icon} onClick={() => onReview(fresh[0]!.proposalId!)}>
        {label}
      </Button>
    );
  }
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" icon={icon} aria-label={failedHint ? `${label}. ${failedHint}` : `${label}: show them`} title={failedHint}>
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 max-w-(--radix-popover-content-available-width) p-1">
        <AiJobsBanner jobs={jobs} onReview={onReview} onOpenAll={onOpenAll} bare />
      </PopoverContent>
    </Popover>
  );
}

/** Every recent job, with cost: the list in the review tab. */
export function JobsList({ jobs, onReview }: { jobs: readonly AiJob[]; onReview: (proposalId: Id) => void }) {
  if (!jobs.length) {
    return (
      <p className="flex items-center gap-2 px-4 py-3 text-ui text-fg-subtle">
        <Bot className="size-4" aria-hidden />
        No AI jobs yet. Import a document, or ask AI to draft or improve an item.
      </p>
    );
  }
  const total = jobs.reduce((n, j) => n + (j.cost ?? 0), 0);
  const oneMachine = new Set(jobs.map((j) => j.machineId)).size <= 1;
  const oneModel = new Set(jobs.map((j) => j.model)).size <= 1;
  return (
    <div className="flex flex-col">
      <ul data-list aria-label="AI jobs">
        {jobs.map((j) => (
          <JobRow key={j.id} job={j} onReview={onReview} showMachine={!(oneMachine && jobs.length > 1)} showModel={!(oneModel && jobs.length > 1)} />
        ))}
      </ul>
      {total > 0 && <p className="px-4 py-2 text-right text-caption text-fg-subtle num">Total {formatMoney(total)}</p>}
    </div>
  );
}

/**
 * One job of the list: what it is, then one caption (its progress, what it waits for, or what
 * failed and what to do next, whole), and its one action as an xs icon button on hover or focus,
 * like every row of the tab. A job that waits for a landing starts now from that same slot.
 */
function JobRow({ job: j, onReview, showMachine, showModel }: { job: AiJob; onReview: (proposalId: Id) => void; showMachine: boolean; showModel: boolean }) {
  const now = useNow(1000);
  const machines = useMachines();
  const navigate = useNavigate();
  const actions = useJobActions();
  const projects = useOrgus((s) => s.projects);
  const hold = useHold('ai-job', j.state === 'queued' ? { id: j.id, label: j.title, projectId: j.projectId, machineId: j.machineId } : null);
  const [holdError, setHoldError] = useState<UiError | null>(null);
  const [starting, setStarting] = useState(false);
  const err = actions.errorFor(j.id) ?? holdError;
  const where = [showMachine ? (machines.find((m) => m.id === j.machineId)?.name ?? j.machineId) : null, showModel ? j.model : null].filter(Boolean).join(' with ');
  const live = isJobLive(j.state);
  const failed = j.state === 'failed' && j.error;
  const noMachine = failed && /no machine/i.test(j.error!.why);
  // The progress line only while it runs and has moved: a waiting job draws no empty bar.
  const progress = live && j.state !== 'queued' ? jobProgress(j) : 0;
  const caption = failed ? `${j.error!.why} ${j.error!.next}` : hold ? holdText(hold, (id) => projects[id]?.name) : null;
  const startNow = async () => {
    if (!hold) return;
    setStarting(true);
    setHoldError(null);
    try {
      await startHoldNow(hold.id);
    } catch (e) {
      setHoldError(toUiError(e));
    } finally {
      setStarting(false);
    }
  };
  return (
    <li className={cn(REVEAL_GROUP, 'relative border-b border-border-subtle last:border-b-0')}>
      {/* A failure keeps what failed and what to do next whole: the caption wraps, so the row grows to hold it. */}
      <div data-row className={cn('relative flex items-center gap-3 px-4 transition-colors duration-(--duration-fast) hover:bg-hover', caption ? 'min-h-row-2line py-2' : 'min-h-row-2line py-2 sm:h-row-2line sm:py-0')}>
        <JobIcon job={j} />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="line-clamp-2 text-ui text-fg wrap-anywhere sm:line-clamp-none sm:truncate">{j.title}</span>
          <span className={cn('text-caption', caption ? 'text-fg-muted' : 'truncate text-fg-subtle')} data-meta>
            {caption ?? <JobLine job={j} now={now} />}
          </span>
        </span>
        {where && (
          <span className="hidden w-32 shrink-0 truncate text-caption text-fg-subtle md:block" data-column="where">
            {where}
          </span>
        )}
        <RevealRest className="hidden w-20 text-caption text-fg-subtle sm:flex">
          <RelativeTime ts={j.createdAt} />
        </RevealRest>
        {progress > 0 && (
          <span aria-hidden className="absolute inset-x-0 bottom-0 h-0.5 bg-hover">
            <span className="block h-full bg-status-working" style={{ width: `${Math.round(progress * 100)}%` }} />
          </span>
        )}
      </div>
      <RevealSlot className="absolute right-4 top-0 flex h-row-2line">
        {hold ? (
          <IconButton size="xs" label={`Start ${j.title} now`} icon={<Play />} loading={starting} onClick={() => void startNow()} />
        ) : live ? (
          <IconButton size="xs" label={`Cancel ${j.title}`} icon={<X />} onClick={() => actions.cancel(j)} />
        ) : j.proposalId ? (
          <IconButton size="xs" label={`Open the proposal of ${j.title}`} icon={<Eye />} onClick={() => onReview(j.proposalId!)} />
        ) : noMachine ? (
          <IconButton size="xs" label="Open the machines page" icon={<Server />} onClick={() => navigate('/machines')} />
        ) : (
          <IconButton size="xs" label={`Clear ${j.title}`} icon={<Trash2 />} onClick={() => actions.clear(j)} />
        )}
      </RevealSlot>
      {err && (
        <div className="px-4 pb-2">
          <ErrorState variant="inline" error={err} />
        </div>
      )}
    </li>
  );
}
